import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MagnifyingGlass,
  Hoodie,
  Sparkle,
  Storefront,
  Tag,
  Funnel,
  X,
} from "@phosphor-icons/react";
import { browseMarketplace, formatPrice, imageSrc } from "../lib/api";

const CONDITIONS = ["New with tags", "Like new", "Very good", "Good", "Used"];

const Card = ({ item }) => (
  <Link
    to={`/listing/${item.id}`}
    data-testid={`listing-card-${item.id}`}
    className="nb-card nb-card-hover block bg-white"
  >
    <div className="relative flex h-52 w-full items-center justify-center overflow-hidden rounded-lg border-[3px] border-[#0A0A0A] bg-[#FDFBF7]">
      {imageSrc(item) ? (
        <img src={imageSrc(item)} alt={item.product_name} className="h-full w-full object-cover" />
      ) : (
        <Hoodie weight="duotone" size={56} />
      )}
      <span className="absolute left-3 top-3 nb-badge bg-[#B4F0A7]">{item.stock} left</span>
    </div>
    <div className="mt-4 flex items-center justify-between">
      <span className="font-display text-xl font-black tracking-tighter">{formatPrice(item.listing_price)}</span>
      <span className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">{item.brand || "Unbranded"}</span>
    </div>
    <h3 className="mt-1 line-clamp-2 font-display text-base font-bold leading-tight">
      {item.suggested_title || item.product_name}
    </h3>
    <div className="mt-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-[#4A4A4A]">
      <Tag weight="bold" size={12} />
      {item.category || "—"} · @{item.seller_name || "seller"}
    </div>
  </Link>
);

const FiltersPanel = ({ filters, setFilters, apply, reset }) => (
  <div className="nb-card bg-[#FDFBF7] space-y-4">
    <div className="flex items-center gap-2">
      <Funnel weight="duotone" size={20} />
      <span className="nb-label">Filters</span>
    </div>
    <div>
      <label className="nb-label">Condition</label>
      <div className="mt-2 flex flex-wrap gap-2">
        {CONDITIONS.map((c) => (
          <button
            key={c}
            data-testid={`filter-condition-${c.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setFilters((f) => ({ ...f, condition: f.condition === c ? "" : c }))}
            className={`nb-badge cursor-pointer ${filters.condition === c ? "bg-[#D4C4FB]" : "bg-white"}`}
          >
            {c}
          </button>
        ))}
      </div>
    </div>
    <div>
      <label className="nb-label">Size</label>
      <input
        data-testid="filter-size"
        value={filters.size || ""}
        onChange={(e) => setFilters((f) => ({ ...f, size: e.target.value }))}
        placeholder="e.g. M, L, UK 10"
        className="nb-input mt-2"
      />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="nb-label">Min £</label>
        <input
          data-testid="filter-min-price"
          type="number"
          value={filters.min_price || ""}
          onChange={(e) => setFilters((f) => ({ ...f, min_price: e.target.value }))}
          className="nb-input mt-2"
        />
      </div>
      <div>
        <label className="nb-label">Max £</label>
        <input
          data-testid="filter-max-price"
          type="number"
          value={filters.max_price || ""}
          onChange={(e) => setFilters((f) => ({ ...f, max_price: e.target.value }))}
          className="nb-input mt-2"
        />
      </div>
    </div>
    <div className="flex gap-2">
      <button data-testid="apply-filters" onClick={apply} className="nb-btn flex-1">
        Apply
      </button>
      <button data-testid="reset-filters" onClick={reset} className="nb-btn nb-btn-ghost">
        <X weight="bold" size={16} />
      </button>
    </div>
  </div>
);

export default function Marketplace() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ condition: "", size: "", min_price: "", max_price: "" });
  const [showFilters, setShowFilters] = useState(false);

  const buildParams = (search = q) => {
    const p = {};
    if (search) p.q = search;
    if (filters.condition) p.condition = filters.condition;
    if (filters.size) p.size = filters.size;
    if (filters.min_price) p.min_price = filters.min_price;
    if (filters.max_price) p.max_price = filters.max_price;
    return p;
  };

  const load = async (params) => {
    setLoading(true);
    try {
      const r = await browseMarketplace(params);
      setItems(r.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(buildParams(""));
    // eslint-disable-next-line
  }, []);

  const apply = () => load(buildParams());
  const reset = () => {
    setFilters({ condition: "", size: "", min_price: "", max_price: "" });
    setQ("");
    load({});
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 sm:px-10">
      <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
        <div>
          <span className="nb-label">Marketplace</span>
          <h1 className="mt-2 font-display text-4xl font-black leading-none tracking-tighter sm:text-5xl lg:text-6xl">
            Pre-loved finds,<br />priced to flip.
          </h1>
          <p className="mt-3 max-w-xl text-base font-medium text-[#4A4A4A]">
            Shop second-hand from independent sellers. Ships with Royal Mail or Evri.
          </p>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            apply();
          }}
          className="flex w-full items-center gap-3 lg:w-auto"
        >
          <div className="relative flex-1 lg:w-80">
            <MagnifyingGlass weight="bold" size={18} className="absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search brand, name, keyword…"
              className="nb-input w-full pl-11"
            />
          </div>
          <button data-testid="search-btn" type="submit" className="nb-btn">Search</button>
          <button
            type="button"
            data-testid="toggle-filters"
            onClick={() => setShowFilters((v) => !v)}
            className="nb-btn nb-btn-ghost lg:hidden"
          >
            <Funnel weight="bold" size={18} />
          </button>
        </form>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[280px_1fr]">
        <aside className={`${showFilters ? "block" : "hidden"} lg:block`}>
          <FiltersPanel filters={filters} setFilters={setFilters} apply={apply} reset={reset} />
        </aside>

        <div>
          {loading ? (
            <div className="nb-card flex items-center gap-3 bg-white">
              <Sparkle weight="duotone" size={22} className="animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="nb-card flex flex-col items-center gap-4 bg-[#FFDF85] text-center" style={{ borderStyle: "dashed" }}>
              <Storefront weight="duotone" size={48} />
              <h3 className="font-display text-2xl font-black tracking-tight">Nothing matches</h3>
              <p className="max-w-md text-sm font-medium">Try adjusting filters or browse all listings.</p>
              <button data-testid="empty-cta" onClick={reset} className="nb-btn">Clear filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((it) => <Card key={it.id} item={it} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
